import { useEffect, useState } from 'react';
import { X, Plus, Trash2, Save, Loader2, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

interface Item {
  name: string;
  quantity: number;
  unit: string;
  notes?: string;
}

const UNITS = ['each', 'm', 'roll', 'box', 'ltr', 'kg', 'pair', 'tube', 'pack'];

export default function CreatePartsOrderModal({
  jobId,
  onClose,
  onCreated,
}: {
  jobId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [supplier, setSupplier] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<Item[]>([{ name: '', quantity: 1, unit: 'each' }]);
  const [suggestions, setSuggestions] = useState<{ name: string; unit: string; default_price?: number }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/consumables');
        setSuggestions(res.data || []);
      } catch {}
    })();
  }, []);

  const updateItem = (i: number, patch: Partial<Item>) => {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((arr) => [...arr, { name: '', quantity: 1, unit: 'each' }]);
  const removeItem = (i: number) =>
    setItems((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const submit = async () => {
    const filtered = items.filter((it) => it.name.trim() && it.quantity > 0);
    if (filtered.length === 0) return toast.error('Add at least one item');
    setSaving(true);
    try {
      const res = await api.post('/parts-orders', {
        job_id: jobId,
        supplier: supplier.trim() || null,
        notes: notes.trim() || null,
        items: filtered.map((it) => ({
          name: it.name.trim(),
          quantity: Number(it.quantity),
          unit: it.unit,
          notes: it.notes?.trim() || null,
        })),
      });
      toast.success(`${res.data.order_number} created`);
      onCreated();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-pop overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 grid place-items-center">
              <Truck className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">New parts order</h2>
              <p className="text-xs text-slate-500">A PO number will be generated automatically.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Supplier / merchant
            </label>
            <input
              className="input"
              placeholder="e.g. Plumbworld, City Plumbing, Screwfix…"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Items ({items.length})
              </label>
              <button
                onClick={addItem}
                type="button"
                className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg"
                >
                  <input
                    className="input flex-1"
                    list={`sugg-${i}`}
                    placeholder="Part name (e.g. 15mm Copper Elbow)"
                    value={it.name}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateItem(i, { name: val });
                      const match = suggestions.find(
                        (s) => s.name.toLowerCase() === val.toLowerCase()
                      );
                      if (match) updateItem(i, { unit: match.unit });
                    }}
                  />
                  <datalist id={`sugg-${i}`}>
                    {suggestions.slice(0, 200).map((s) => (
                      <option key={s.name} value={s.name} />
                    ))}
                  </datalist>
                  <input
                    type="number"
                    min={0.5}
                    step={0.5}
                    className="input w-24"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })}
                  />
                  <select
                    className="input w-28"
                    value={it.unit}
                    onChange={(e) => updateItem(i, { unit: e.target.value })}
                  >
                    {UNITS.map((u) => (
                      <option key={u}>{u}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    disabled={items.length === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Notes (optional)
            </label>
            <textarea
              className="input min-h-[80px] resize-y"
              placeholder="e.g. Order Fri collect Mon, delivery to yard, specific brand…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            💡 Job status will auto-flip to <strong>Awaiting Parts</strong> when this order is
            created. Mark items received → job returns to <strong>In Progress</strong>.
          </div>
        </div>

        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button onClick={submit} className="btn btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Create order
          </button>
        </div>
      </div>
    </div>
  );
}
